import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Modal,
  Form,
  Alert,
  Spinner,
  Badge,
  Accordion,
  ListGroup,
  ListGroupItem
} from 'react-bootstrap';
import { menuManagementAPI, authAPI } from '../api/api';
import apiClient from '../api/api';
import { AuthContext } from '../context/AuthContext';
import './MenuManagement.css';

function MenuManagement() {
  const { user, setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [menuItems, setMenuItems] = useState([]);
  const [tables, setTables] = useState([]);
  const [parentFolders, setParentFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    folder: false,
    linkRecordID: null,
    parentFolderID: null,
    link: ''
  });

  const [editData, setEditData] = useState({
    menuItemID: null,
    name: '',
    folder: false,
    linkRecordID: null,
    parentFolderID: null,
    link: ''
  });

  // Fetch menu items and tables
  const fetchMenuItems = async () => {
    try {
      setLoading(true);
      const response = await menuManagementAPI.getMenuItems();
      if (response.data.success) {
        const items = response.data.items || response.data.menuItems || [];
        setMenuItems(items);
        // Don't set tables here - fetchTables() handles that separately
      } else {
        setError('Failed to load menu items: ' + (response.data.message || 'Unknown error'));
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to load menu items';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Fetch available tables from MGB_tables (for menu management)
  const fetchTables = async () => {
    try {
      // Fetch tables from MGB_tables which has tableID
      // Add cache-busting to ensure fresh data
      const response = await apiClient.get('/mgb-setup/tables', {
        params: { _t: Date.now() }
      });
      
      if (response.data && response.data.success) {
        const tableList = response.data.tables || [];
        setTables(tableList);
        return; // Success, exit early
      }
      
      // If we get here, response wasn't successful
      throw new Error('MGB tables response not successful');
    } catch (err) {
      // Don't fallback to regular tables API - that doesn't have tableID
      // Instead, show error but keep trying
      if (err.response?.status === 401) {
        setError('Session expired. Please login again.');
      } else {
        setError('Failed to load tables. Please refresh the page.');
      }
      setTables([]);
    }
  };

  // Fetch parent folders
  const fetchParentFolders = async () => {
    try {
      const response = await menuManagementAPI.getParentFolders();
      if (response.data.success) {
        const folders = response.data.folders || response.data.items || [];
        setParentFolders(Array.isArray(folders) ? folders : []);
      } else {
        setParentFolders([]);
      }
    } catch (err) {
      setParentFolders([]);
    }
  };

  // Handle Add Menu Item
  const handleAddMenuItem = async () => {
    if (!formData.name) {
      setError('Menu name is required');
      return;
    }

    try {
      setError('');
      const response = await menuManagementAPI.createMenuItem({
        name: formData.name,
        folder: formData.folder,
        linkRecordID: formData.folder ? null : formData.linkRecordID,
        parentFolderID: formData.parentFolderID,
        link: formData.link || null
      });

      if (response.data.success) {
        setSuccess('Menu item created successfully');
        setShowAddModal(false);
        setFormData({
          name: '',
          folder: false,
          linkRecordID: null,
          parentFolderID: null,
          link: ''
        });
        fetchMenuItems();
        fetchParentFolders();
        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Error creating menu item';
      setError(errorMsg);
      }
  };

  // Handle Edit Menu Item
  const handleEditMenuItem = async () => {
    if (!editData.name) {
      setError('Menu name is required');
      return;
    }

    try {
      setError('');
      const response = await menuManagementAPI.updateMenuItem(editData.menuItemID, {
        name: editData.name,
        folder: editData.folder,
        linkRecordID: editData.folder ? null : editData.linkRecordID,
        parentFolderID: editData.parentFolderID,
        link: editData.link || null
      });

      if (response.data.success) {
        setSuccess('Menu item updated successfully');
        setShowEditModal(false);
        fetchMenuItems();
        fetchParentFolders();
        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Error updating menu item';
      setError(errorMsg);
      }
  };

  // Handle Delete Menu Item
  const handleDeleteMenuItem = async (menuItemID) => {
    if (!window.confirm('Are you sure you want to delete this menu item?')) {
      return;
    }

    try {
      setError('');
      const response = await menuManagementAPI.deleteMenuItem(menuItemID);

      if (response.data.success) {
        setSuccess('Menu item deleted successfully');
        fetchMenuItems();
        fetchParentFolders();
        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Error deleting menu item';
      setError(errorMsg);
      }
  };

  // Handle Reorder Menu Item
  const handleReorderItem = async (menuItemID, direction) => {
    try {
      setError('');
      
      const grouped = groupItemsByFolder();
      let itemsList = [];
      let currentItem = null;
      
      // Find which list the item belongs to
      currentItem = menuItems.find(m => m.MenuItemID === menuItemID);
      
      if (currentItem.ParentFolderID) {
        // Item is in a folder
        itemsList = grouped.folders[currentItem.ParentFolderID] || [];
      } else {
        // Item is top-level (only folders and non-folder items without parent)
        itemsList = grouped.topLevel;
      }
      
      const currentIndex = itemsList.findIndex(m => m.MenuItemID === menuItemID);
      
      if (direction === 'up' && currentIndex > 0) {
        // Swap with previous item
        const prevItem = itemsList[currentIndex - 1];
        const temp = currentItem.MenuItemOrder;
        currentItem.MenuItemOrder = prevItem.MenuItemOrder;
        prevItem.MenuItemOrder = temp;
        
        // Update both items
        await menuManagementAPI.updateMenuItem(currentItem.MenuItemID, {
          ...currentItem,
          MenuItemOrder: currentItem.MenuItemOrder
        });
        await menuManagementAPI.updateMenuItem(prevItem.MenuItemID, {
          ...prevItem,
          MenuItemOrder: prevItem.MenuItemOrder
        });
      } else if (direction === 'down' && currentIndex < itemsList.length - 1) {
        // Swap with next item
        const nextItem = itemsList[currentIndex + 1];
        const temp = currentItem.MenuItemOrder;
        currentItem.MenuItemOrder = nextItem.MenuItemOrder;
        nextItem.MenuItemOrder = temp;
        
        // Update both items
        await menuManagementAPI.updateMenuItem(currentItem.MenuItemID, {
          ...currentItem,
          MenuItemOrder: currentItem.MenuItemOrder
        });
        await menuManagementAPI.updateMenuItem(nextItem.MenuItemID, {
          ...nextItem,
          MenuItemOrder: nextItem.MenuItemOrder
        });
      }
      
      setSuccess(`Menu item moved ${direction}`);
      fetchMenuItems();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Error reordering menu item';
      setError(errorMsg);
      }
  };

  // Open edit modal
  const openEditModal = (item) => {
    setEditData({
      menuItemID: item.MenuItemID,
      name: item.Name,
      folder: item.Folder,
      linkRecordID: item.LinkRecordID,
      parentFolderID: item.ParentFolderID,
      link: item.Link || ''
    });
    setShowEditModal(true);
  };

  // Get table display name
  const getTableName = (tableID) => {
    const table = tables.find(t => t.tableID === tableID);
    return table ? (table.table1 || table.TableName) : 'N/A';
  };

  // Get parent folder name
  const getParentFolderName = (parentFolderID) => {
    const folder = menuItems.find(m => m.MenuItemID === parentFolderID);
    return folder ? folder.Name : 'N/A';
  };

  // Group items by parent folder
  const groupItemsByFolder = () => {
    const grouped = {
      topLevel: [],
      folders: {}
    };

    menuItems.forEach(item => {
      if (item.ParentFolderID) {
        if (!grouped.folders[item.ParentFolderID]) {
          grouped.folders[item.ParentFolderID] = [];
        }
        grouped.folders[item.ParentFolderID].push(item);
      } else {
        grouped.topLevel.push(item);
      }
    });

    return grouped;
  };

  // Render menu item with edit/delete buttons
  const renderMenuItem = (item, isNested = false) => (
    <div key={item.MenuItemID} className={`menu-item-row ${isNested ? 'nested-item' : ''}`}>
      <div className="menu-item-content">
        <div className="menu-item-info">
          <span className="menu-icon">
            {item.Folder ? '📁' : '📄'}
          </span>
          <div className="menu-item-details">
            <strong>{item.Name}</strong>
            <small className="text-muted d-block">
              {item.Folder ? (
                <Badge bg="info">Folder</Badge>
              ) : (
                <>
                  <Badge bg="success">Item</Badge>
                  {item.LinkRecordID && (
                    <span className="ms-2">Table: {getTableName(item.LinkRecordID)}</span>
                  )}
                  {item.Link && (
                    <span className="ms-2">Link: {item.Link}</span>
                  )}
                </>
              )}
            </small>
          </div>
        </div>
        <div className="menu-item-actions">
          <div className="reorder-buttons">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => handleReorderItem(item.MenuItemID, 'up')}
              title="Move Up"
              className="reorder-btn"
            >
              ▲
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => handleReorderItem(item.MenuItemID, 'down')}
              title="Move Down"
              className="reorder-btn"
            >
              ▼
            </Button>
          </div>
          <Button
            variant="warning"
            size="sm"
            onClick={() => openEditModal(item)}
            className="me-2"
          >
            Edit
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => handleDeleteMenuItem(item.MenuItemID)}
          >
            Delete
          </Button>
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    const loadData = async () => {
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        setLoading(true);
        // First verify session is still valid
        const sessionResponse = await authAPI.checkSession();
        if (sessionResponse.data.success && sessionResponse.data.user) {
          // Update user in context if session has updated info
          setUser(sessionResponse.data.user);
        }
        
        // Session is valid, now fetch data
        // Fetch tables first, then menu items (so tables are available when rendering)
        await fetchTables();
        await fetchMenuItems();
        await fetchParentFolders();
      } catch (err) {
        // If session check fails (401), clear user and redirect to login
        if (err.response?.status === 401) {
          setUser(null);
          navigate('/login');
          return;
        }
        
        // Still try to fetch menu items even if tables fail (unless it's auth error)
        if (err.response?.status !== 401) {
          await fetchMenuItems();
          await fetchParentFolders();
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []); // Empty dependency array - only run once on mount

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container fluid className="menu-management-page py-4">
      <Row className="mb-4">
        <Col>
          <h1 className="mb-3">📋 Manage Menu Management</h1>
          <p className="text-muted">
            Create and manage menu items and folder structure for the application menu.
          </p>
        </Col>
      </Row>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

      <Row className="mb-4">
        <Col>
          <Button
            variant="primary"
            onClick={() => {
              setFormData({
                name: '',
                folder: false,
                linkRecordID: null,
                parentFolderID: null,
                link: ''
              });
              setShowAddModal(true);
            }}
          >
            + Add Menu Item
          </Button>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card>
            <Card.Header className="bg-light">
              <Card.Title className="mb-0">Menu Items</Card.Title>
            </Card.Header>
            <Card.Body>
              {menuItems.length > 0 ? (
                <div className="menu-accordion-container">
                  <Accordion defaultActiveKey={null} flush>
                    {(() => {
                      const grouped = groupItemsByFolder();
                      let accordionIndex = 0;

                      return (
                        <>
                          {/* Top-level items */}
                          {grouped.topLevel.map((item) => (
                            item.Folder ? (
                              // Folder with children
                              <Accordion.Item eventKey={String(accordionIndex++)} key={item.MenuItemID}>
                                <Accordion.Header className="menu-folder-header">
                                  <span className="folder-icon">📁</span>
                                  <strong>{item.Name}</strong>
                                  <Badge bg="info" className="ms-2">
                                    Folder ({grouped.folders[item.MenuItemID]?.length || 0} items)
                                  </Badge>
                                </Accordion.Header>
                                <Accordion.Body className="menu-folder-body">
                                  <div className="folder-actions mb-2">
                                    <div className="reorder-buttons">
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => handleReorderItem(item.MenuItemID, 'up')}
                                        title="Move Folder Up"
                                        className="reorder-btn"
                                      >
                                        ▲
                                      </Button>
                                      <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => handleReorderItem(item.MenuItemID, 'down')}
                                        title="Move Folder Down"
                                        className="reorder-btn"
                                      >
                                        ▼
                                      </Button>
                                    </div>
                                    <Button
                                      variant="warning"
                                      size="sm"
                                      onClick={() => openEditModal(item)}
                                      className="me-2"
                                    >
                                      Edit Folder
                                    </Button>
                                    <Button
                                      variant="danger"
                                      size="sm"
                                      onClick={() => handleDeleteMenuItem(item.MenuItemID)}
                                    >
                                      Delete Folder
                                    </Button>
                                  </div>
                                  <ListGroup variant="flush" className="nested-items-list">
                                    {grouped.folders[item.MenuItemID]?.map((childItem) =>
                                      renderMenuItem(childItem, true)
                                    )}
                                    {!grouped.folders[item.MenuItemID] || grouped.folders[item.MenuItemID].length === 0 && (
                                      <p className="text-muted ps-3 py-2">No items in this folder</p>
                                    )}
                                  </ListGroup>
                                </Accordion.Body>
                              </Accordion.Item>
                            ) : (
                              // Top-level item without folder
                              <div key={item.MenuItemID} className="top-level-item mb-3">
                                {renderMenuItem(item, false)}
                              </div>
                            )
                          ))}
                        </>
                      );
                    })()}
                  </Accordion>
                </div>
              ) : (
                <p className="text-muted">No menu items found</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Add Menu Item Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add Menu Item</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Menu Name *</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="Enter menu name"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Is Folder"
                checked={formData.folder}
                onChange={(e) =>
                  setFormData({ 
                    ...formData, 
                    folder: e.target.checked,
                    linkRecordID: e.target.checked ? null : formData.linkRecordID
                  })
                }
              />
              <Form.Text className="text-muted">
                Check if this is a folder that can contain other menu items
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Parent Folder (Optional)</Form.Label>
              <Form.Select
                value={formData.parentFolderID || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    parentFolderID: e.target.value ? parseInt(e.target.value) : null
                  })
                }
              >
                <option value="">-- None (Top Level) --</option>
                {Array.isArray(parentFolders) && parentFolders.map((folder) => (
                  <option key={folder.MenuItemID} value={folder.MenuItemID}>
                    {folder.Name}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Select a parent folder to place this item under
              </Form.Text>
            </Form.Group>

            {!formData.folder && (
              <Form.Group className="mb-3">
                <Form.Label>Linked Table (Optional)</Form.Label>
                <Form.Select
                  value={formData.linkRecordID || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      linkRecordID: e.target.value ? parseInt(e.target.value) : null
                    })
                  }
                >
                  <option value="">-- No Link --</option>
                  {tables && tables.length > 0 ? (
                    tables.map((table) => {
                      const tableID = table.tableID || table.TableID || table.id;
                      const displayName = table.table1 || table.TableName || table.ResolvedName || `Table ID: ${tableID}`;
                      return (
                        <option key={tableID} value={tableID}>
                          {displayName}
                        </option>
                      );
                    })
                  ) : (
                    <option value="" disabled>Loading tables...</option>
                  )}
                </Form.Select>
                <Form.Text className="text-muted">
                  Select a table to link this menu item to
                </Form.Text>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Custom Link (Optional)</Form.Label>
              <Form.Control
                type="text"
                value={formData.link}
                onChange={(e) =>
                  setFormData({ ...formData, link: e.target.value })
                }
                placeholder="e.g., /custom-page"
              />
              <Form.Text className="text-muted">
                Custom URL link for this menu item
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAddMenuItem}>
            Add Menu Item
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Menu Item Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Menu Item</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Menu Name *</Form.Label>
              <Form.Control
                type="text"
                value={editData.name}
                onChange={(e) =>
                  setEditData({ ...editData, name: e.target.value })
                }
                placeholder="Enter menu name"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Is Folder"
                checked={editData.folder}
                onChange={(e) =>
                  setEditData({ 
                    ...editData, 
                    folder: e.target.checked,
                    linkRecordID: e.target.checked ? null : editData.linkRecordID
                  })
                }
              />
              <Form.Text className="text-muted">
                Check if this is a folder that can contain other menu items
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Parent Folder (Optional)</Form.Label>
              <Form.Select
                value={editData.parentFolderID || ''}
                onChange={(e) =>
                  setEditData({
                    ...editData,
                    parentFolderID: e.target.value ? parseInt(e.target.value) : null
                  })
                }
              >
                <option value="">-- None (Top Level) --</option>
                {Array.isArray(parentFolders) && parentFolders
                  .filter(f => f.MenuItemID !== editData.menuItemID) // Prevent circular reference
                  .map((folder) => (
                    <option key={folder.MenuItemID} value={folder.MenuItemID}>
                      {folder.Name}
                    </option>
                  ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Select a parent folder to place this item under
              </Form.Text>
            </Form.Group>

            {!editData.folder && (
              <Form.Group className="mb-3">
                <Form.Label>Linked Table (Optional)</Form.Label>
                <Form.Select
                  value={editData.linkRecordID || ''}
                  onChange={(e) =>
                    setEditData({
                      ...editData,
                      linkRecordID: e.target.value ? parseInt(e.target.value) : null
                    })
                  }
                >
                  <option value="">-- No Link --</option>
                  {tables && tables.length > 0 ? (
                    tables.map((table) => {
                      const tableID = table.tableID || table.TableID || table.id;
                      const displayName = table.table1 || table.TableName || table.ResolvedName || `Table ID: ${tableID}`;
                      return (
                        <option key={tableID} value={tableID}>
                          {displayName}
                        </option>
                      );
                    })
                  ) : (
                    <option value="" disabled>Loading tables...</option>
                  )}
                </Form.Select>
                <Form.Text className="text-muted">
                  Select a table to link this menu item to
                </Form.Text>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Custom Link (Optional)</Form.Label>
              <Form.Control
                type="text"
                value={editData.link}
                onChange={(e) =>
                  setEditData({ ...editData, link: e.target.value })
                }
                placeholder="e.g., /custom-page"
              />
              <Form.Text className="text-muted">
                Custom URL link for this menu item
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleEditMenuItem}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default MenuManagement;

